import {
  CognitoUserPool, CognitoUser, AuthenticationDetails, type CognitoUserSession,
} from "amazon-cognito-identity-js";

/**
 * The session persists in localStorage so a reload or app relaunch keeps you
 * signed in — the refresh token (valid 30 days) mints fresh id tokens as needed.
 */
const pool = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_USER_POOL_ID,
  ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
  Storage: window.localStorage,
});

let session: CognitoUserSession | null = null;

export function signIn(email: string, password: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: pool, Storage: window.localStorage });
  const details = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (s) => { session = s; resolve(); },
      onFailure: (err) => reject(new Error(readableAuthError(err))),
      newPasswordRequired: () => reject(new Error("Set a new password in the Cognito console, then sign in again")),
    });
  });
}

/** Rehydrate the session from a stored login. Call once on app start. */
export function restoreSession(): Promise<boolean> {
  const user = pool.getCurrentUser();
  if (!user) return Promise.resolve(false);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, s: CognitoUserSession | null) => {
      if (err || !s || !s.isValid()) { resolve(false); return; }
      session = s;
      resolve(true);
    });
  });
}

export function signOut(): void {
  pool.getCurrentUser()?.signOut();
  session = null;
}

export function isSignedIn(): boolean {
  return Boolean(session?.isValid());
}

export async function idToken(): Promise<string> {
  if (session?.isValid()) return session.getIdToken().getJwtToken();

  const user = pool.getCurrentUser();
  if (!user) throw new Error("Sign in to continue");

  // getSession auto-refreshes with the refresh token when the id token is stale.
  return new Promise((resolve, reject) => {
    user.getSession((err: Error | null, s: CognitoUserSession | null) => {
      if (err || !s) { reject(new Error("Your session expired. Sign in again.")); return; }
      session = s;
      resolve(s.getIdToken().getJwtToken());
    });
  });
}

function readableAuthError(err: unknown): string {
  const code = (err as { code?: string }).code ?? "";
  if (code === "NotAuthorizedException") return "That email and password do not match";
  if (code === "UserNotFoundException") return "No account with that email";
  if (code === "PasswordResetRequiredException") return "Reset your password to continue";
  return (err as Error).message ?? "Sign-in failed";
}
